/**
 * Wizard Configuration System
 * Defines contextual recommendations and guidance for different pages and user states
 */

import {
  Target,
  Map,
  Mountain,
  BookOpen,
  Search,
  Users,
  TrendingUp,
  Play,
  Database,
  Star,
  Award
} from 'lucide-react';

export interface WizardRecommendation {
  id: string;
  type: 'explore' | 'develop' | 'understand' | 'search' | 'learn' | 'action' | 'collaborate';
  title: string;
  description: string;
  action: {
    type: 'navigate' | 'highlight' | 'tutorial' | 'modal' | 'external';
    target: string;
    data?: unknown;
  };
  priority: 'high' | 'medium' | 'low';
  icon: React.ElementType;
  color: string;
  conditions?: {
    minVisitedPages?: number;
    maxVisitedPages?: number;
    requiredPages?: string[];
    excludedPages?: string[];
    minOutcomes?: number;
    maxOutcomes?: number;
  };
}

export interface PageContext {
  path: string;
  name: string;
  category: string;
  description: string;
  recommendations: WizardRecommendation[];
  quickActions: {
    id: string;
    label: string;
    icon: React.ElementType;
    action: string;
    color: string;
  }[];
  contextualMessage: (context: WizardPageContextData) => string;
}

// Page-specific configurations
export interface WizardPageContextData {
  visitedPages: string[];
  outcomes: unknown[];
  lastActivity: Date;
}

/** PAGE_CONTEXTS keys are flat-era paths; strip the /elevation prefix first. */
export function normalizeWizardPath(pathname: string): string {
  return pathname.replace(/^\/elevation(?=\/|$)/, '') || '/';
}

export const PAGE_CONTEXTS: Record<string, PageContext> = {
  '/': {
    path: '/',
    name: 'Mission Control',
    category: 'navigation',
    description: 'Central hub for choosing your learning journey',
    recommendations: [
      {
        id: 'first-visit-overview',
        type: 'understand',
        title: 'Start with the Big Picture',
        description: 'Explore the MOF Architecture to understand how everything fits together',
        action: { type: 'navigate', target: '/overview' },
        priority: 'high',
        icon: Target,
        color: 'primary',
        conditions: { maxVisitedPages: 1 }
      },
      {
        id: 'explore-island',
        type: 'explore',
        title: 'Discover Capabilities',
        description: 'Navigate the Island to explore the 12 capability dimensions',
        action: { type: 'navigate', target: '/island' },
        priority: 'high',
        icon: Map,
        color: 'sage',
        conditions: { excludedPages: ['/island'] }
      },
      {
        id: 'climb-mountain',
        type: 'develop',
        title: 'Track Your Progress',
        description: 'Climb the Mountain to see your DEGAP stage progression',
        action: { type: 'navigate', target: '/mountain' },
        priority: 'medium',
        icon: Mountain,
        color: 'primary',
        conditions: { excludedPages: ['/mountain'] }
      }
    ],
    quickActions: [
      { id: 'search', label: 'Search', icon: Search, action: '/advanced-search', color: 'primary' },
      { id: 'glossary', label: 'Glossary', icon: BookOpen, action: '/glossary', color: 'sage' },
      { id: 'progress', label: 'Progress', icon: TrendingUp, action: '/progress', color: 'amber' },
      { id: 'tutorial', label: 'Tutorial', icon: Play, action: 'tutorial', color: 'primary' }
    ],
    contextualMessage: (context) => {
      if (context.visitedPages.length <= 1) {
        return "Welcome! I'm here to help you navigate the Peak Priorities knowledge system.";
      }
      return "Choose your journey based on what you want to accomplish.";
    }
  },

  '/mission-control': {
    path: '/mission-control',
    name: 'Mission Control',
    category: 'navigation',
    description: 'Central hub for choosing your learning journey',
    recommendations: [
      {
        id: 'explore-island-mc',
        type: 'explore',
        title: 'Discover Capabilities',
        description: 'Navigate the Island to explore the 12 capability dimensions',
        action: { type: 'navigate', target: '/island' },
        priority: 'high',
        icon: Map,
        color: 'sage',
        conditions: { excludedPages: ['/island'] }
      },
      {
        id: 'climb-mountain-mc',
        type: 'develop',
        title: 'Track Your Progress',
        description: 'Climb the Mountain to see your DEGAP stage progression',
        action: { type: 'navigate', target: '/mountain' },
        priority: 'medium',
        icon: Mountain,
        color: 'primary',
        conditions: { excludedPages: ['/mountain'] }
      }
    ],
    quickActions: [
      { id: 'search', label: 'Search', icon: Search, action: '/advanced-search', color: 'primary' },
      { id: 'glossary', label: 'Glossary', icon: BookOpen, action: '/glossary', color: 'sage' },
      { id: 'progress', label: 'Progress', icon: TrendingUp, action: '/progress', color: 'amber' },
      { id: 'tutorial', label: 'Tutorial', icon: Play, action: 'tutorial', color: 'primary' }
    ],
    contextualMessage: (_context) => "Choose your journey based on what you want to accomplish."
  },

  '/island': {
    path: '/elevation/island',
    name: 'Island Explorer',
    category: 'exploration',
    description: 'Geographic visualization of 12 capability dimensions',
    recommendations: [
      {
        id: 'explore-models',
        type: 'explore',
        title: 'Dive into Models',
        description: 'Browse the M2 Models to see detailed frameworks',
        action: { type: 'navigate', target: '/models' },
        priority: 'medium',
        icon: BookOpen,
        color: 'primary'
      },
      {
        id: 'try-workshop',
        type: 'develop',
        title: 'Practice with Workshops',
        description: 'Apply what you\'ve learned in hands-on workshops',
        action: { type: 'navigate', target: '/workshops' },
        priority: 'low',
        icon: Play,
        color: 'amber',
        conditions: { minVisitedPages: 3 }
      }
    ],
    quickActions: [
      { id: 'models', label: 'Models', icon: Database, action: '/models', color: 'primary' },
      { id: 'templates', label: 'Templates', icon: Target, action: '/templates', color: 'sage' },
      { id: 'search', label: 'Search', icon: Search, action: '/advanced-search', color: 'primary' },
      { id: 'glossary', label: 'Glossary', icon: BookOpen, action: '/glossary', color: 'sage' }
    ],
    contextualMessage: (_context) => "Explore the 12 capability regions to discover what interests you."
  },

  '/mountain': {
    path: '/elevation/mountain',
    name: 'Mountain Ascent',
    category: 'development',
    description: 'DEGAP stages visualization and navigation',
    recommendations: [
      {
        id: 'access-templates',
        type: 'develop',
        title: 'Get Templates',
        description: 'Access M1 Templates matched to your current elevation',
        action: { type: 'navigate', target: '/templates' },
        priority: 'medium',
        icon: Target,
        color: 'sage'
      },
      {
        id: 'track-outcomes',
        type: 'action',
        title: 'Start Tracking Outcomes',
        description: 'Begin tracking real-world results from your learning',
        action: { type: 'navigate', target: '/outcomes' },
        priority: 'low',
        icon: TrendingUp,
        color: 'amber',
        conditions: { minVisitedPages: 3, maxOutcomes: 0 }
      }
    ],
    quickActions: [
      { id: 'templates', label: 'Templates', icon: Target, action: '/templates', color: 'sage' },
      { id: 'outcomes', label: 'Outcomes', icon: TrendingUp, action: '/outcomes', color: 'amber' },
      { id: 'progress', label: 'Progress', icon: Award, action: '/progress', color: 'primary' },
      { id: 'workshops', label: 'Workshops', icon: Play, action: '/workshops', color: 'amber' }
    ],
    contextualMessage: (_context) => "Track your progression through the 5 DEGAP stages."
  },

  '/models': {
    path: '/elevation/models',
    name: 'M2 Models',
    category: 'exploration',
    description: 'Frameworks and systems',
    recommendations: [
      {
        id: 'try-workshop-models',
        type: 'develop',
        title: 'Practice with Workshops',
        description: 'Apply what you\'ve learned in hands-on workshops',
        action: { type: 'navigate', target: '/workshops' },
        priority: 'medium',
        icon: Play,
        color: 'amber'
      },
      {
        id: 'explore-templates',
        type: 'develop',
        title: 'Get Practical Tools',
        description: 'Access M1 Templates to implement these frameworks',
        action: { type: 'navigate', target: '/templates' },
        priority: 'medium',
        icon: Target,
        color: 'sage'
      }
    ],
    quickActions: [
      { id: 'templates', label: 'Templates', icon: Target, action: '/templates', color: 'sage' },
      { id: 'workshops', label: 'Workshops', icon: Play, action: '/workshops', color: 'amber' },
      { id: 'search', label: 'Search', icon: Search, action: '/advanced-search', color: 'primary' },
      { id: 'glossary', label: 'Glossary', icon: BookOpen, action: '/glossary', color: 'sage' }
    ],
    contextualMessage: (_context) => "Explore detailed frameworks and models to deepen your understanding."
  },

  '/templates': {
    path: '/elevation/templates',
    name: 'M1 Templates',
    category: 'development',
    description: 'Ready-to-deploy resources',
    recommendations: [
      {
        id: 'track-outcomes-templates',
        type: 'action',
        title: 'Track Your Results',
        description: 'Start tracking outcomes from using these templates',
        action: { type: 'navigate', target: '/outcomes' },
        priority: 'medium',
        icon: TrendingUp,
        color: 'amber'
      },
      {
        id: 'explore-team',
        type: 'collaborate',
        title: 'Learn from Others',
        description: 'Explore team stories and collaboration features',
        action: { type: 'navigate', target: '/team' },
        priority: 'low',
        icon: Users,
        color: 'charcoal',
        conditions: { minVisitedPages: 5, excludedPages: ['/team'] }
      }
    ],
    quickActions: [
      { id: 'outcomes', label: 'Outcomes', icon: TrendingUp, action: '/outcomes', color: 'amber' },
      { id: 'workshops', label: 'Workshops', icon: Play, action: '/workshops', color: 'amber' },
      { id: 'team', label: 'Team', icon: Users, action: '/team', color: 'charcoal' },
      { id: 'progress', label: 'Progress', icon: Award, action: '/progress', color: 'primary' }
    ],
    contextualMessage: (_context) => "Access ready-to-use resources matched to your current level and needs."
  },

  '/workshops': {
    path: '/elevation/workshops',
    name: 'Workshops',
    category: 'development',
    description: 'Hands-on learning experiences',
    recommendations: [
      {
        id: 'track-workshop-outcomes',
        type: 'action',
        title: 'Track Workshop Results',
        description: 'Record outcomes from your workshop experiences',
        action: { type: 'navigate', target: '/outcomes' },
        priority: 'high',
        icon: TrendingUp,
        color: 'amber'
      }
    ],
    quickActions: [
      { id: 'outcomes', label: 'Outcomes', icon: TrendingUp, action: '/outcomes', color: 'amber' },
      { id: 'progress', label: 'Progress', icon: Award, action: '/progress', color: 'primary' },
      { id: 'team', label: 'Team', icon: Users, action: '/team', color: 'charcoal' },
      { id: 'analytics', label: 'Analytics', icon: TrendingUp, action: '/analytics', color: 'primary' }
    ],
    contextualMessage: (_context) => "Practice what you've learned through hands-on workshops and exercises."
  },

  '/outcomes': {
    path: '/elevation/outcomes',
    name: 'Outcomes',
    category: 'tracking',
    description: 'Track real-world results and impact',
    recommendations: [
      {
        id: 'explore-team-outcomes',
        type: 'collaborate',
        title: 'Share with Team',
        description: 'Explore team collaboration and sharing features',
        action: { type: 'navigate', target: '/team' },
        priority: 'medium',
        icon: Users,
        color: 'charcoal',
        conditions: { minOutcomes: 1, excludedPages: ['/team'] }
      }
    ],
    quickActions: [
      { id: 'team', label: 'Team', icon: Users, action: '/team', color: 'charcoal' },
      { id: 'analytics', label: 'Analytics', icon: TrendingUp, action: '/analytics', color: 'primary' },
      { id: 'progress', label: 'Progress', icon: Award, action: '/progress', color: 'primary' },
      { id: 'workshops', label: 'Workshops', icon: Play, action: '/workshops', color: 'amber' }
    ],
    contextualMessage: (_context) => "Track and measure the real-world impact of your learning journey."
  },

  '/team': {
    path: '/elevation/team',
    name: 'Team Collaboration',
    category: 'collaboration',
    description: 'Team stories and collaboration features',
    recommendations: [
      {
        id: 'create-story',
        type: 'collaborate',
        title: 'Share Your Story',
        description: 'Create and share your own success story with the team',
        action: { type: 'navigate', target: '/team' },
        priority: 'medium',
        icon: Star,
        color: 'amber'
      }
    ],
    quickActions: [
      { id: 'outcomes', label: 'Outcomes', icon: TrendingUp, action: '/outcomes', color: 'amber' },
      { id: 'analytics', label: 'Analytics', icon: TrendingUp, action: '/analytics', color: 'primary' },
      { id: 'progress', label: 'Progress', icon: Award, action: '/progress', color: 'primary' },
      { id: 'workshops', label: 'Workshops', icon: Play, action: '/workshops', color: 'amber' }
    ],
    contextualMessage: (_context) => "Learn from others' experiences and share your own success stories."
  }
};

// Global recommendations that apply across all pages
export const GLOBAL_RECOMMENDATIONS: WizardRecommendation[] = [
  {
    id: 'track-outcomes-global',
    type: 'action',
    title: 'Start Tracking Outcomes',
    description: 'Begin tracking real-world results from your learning',
    action: { type: 'navigate', target: '/outcomes' },
    priority: 'medium',
    icon: TrendingUp,
    color: 'amber',
    conditions: { minVisitedPages: 3, maxOutcomes: 0 }
  },
  {
    id: 'explore-team-global',
    type: 'collaborate',
    title: 'Learn from Others',
    description: 'Explore team stories and collaboration features',
    action: { type: 'navigate', target: '/team' },
    priority: 'low',
    icon: Users,
    color: 'charcoal',
    conditions: { minVisitedPages: 5, excludedPages: ['/team'] }
  }
];

// Helper function to get recommendations for a specific page and context
export const getRecommendationsForPage = (
  pagePath: string,
  context: {
    visitedPages: string[];
    outcomes: unknown[];
    lastActivity: Date;
  }
): WizardRecommendation[] => {
  const pageContext = PAGE_CONTEXTS[normalizeWizardPath(pagePath)];
  if (!pageContext) return [];

  const allRecommendations = [
    ...pageContext.recommendations,
    ...GLOBAL_RECOMMENDATIONS
  ];

  // Filter recommendations based on conditions
  return allRecommendations.filter(rec => {
    if (!rec.conditions) return true;

    const { visitedPages, outcomes } = context;
    const conditions = rec.conditions;

    // Check visited pages conditions
    if (conditions.minVisitedPages && visitedPages.length < conditions.minVisitedPages) {
      return false;
    }
    if (conditions.maxVisitedPages && visitedPages.length > conditions.maxVisitedPages) {
      return false;
    }

    // Check required pages
    if (conditions.requiredPages) {
      const hasRequiredPages = conditions.requiredPages.every(page => 
        visitedPages.includes(page)
      );
      if (!hasRequiredPages) return false;
    }

    // Check excluded pages
    if (conditions.excludedPages) {
      const hasExcludedPages = conditions.excludedPages.some(page => 
        visitedPages.includes(page)
      );
      if (hasExcludedPages) return false;
    }

    // Check outcomes conditions
    if (conditions.minOutcomes && outcomes.length < conditions.minOutcomes) {
      return false;
    }
    if (conditions.maxOutcomes && outcomes.length > conditions.maxOutcomes) {
      return false;
    }

    return true;
  }).slice(0, 3); // Limit to top 3 recommendations
};

// Helper function to get contextual message for a page
export const getContextualMessage = (
  pagePath: string,
  context: WizardPageContextData
): string => {
  const pageContext = PAGE_CONTEXTS[normalizeWizardPath(pagePath)];
  if (!pageContext) return "I have some recommendations for your next steps.";
  
  return pageContext.contextualMessage(context);
};

// Helper function to get quick actions for a page
export const getQuickActionsForPage = (pagePath: string) => {
  const pageContext = PAGE_CONTEXTS[normalizeWizardPath(pagePath)];
  if (!pageContext) return [];
  
  return pageContext.quickActions;
};
